// useArticleOutline — shared state + edit logic for outline-first rewriting
// (extract → reorder/remove/add → rewrite against the edited outline).
//
// Single source of behavior for two JSX surfaces: the compact OutlinePanel on
// ArticleDetailPage ([[frontend/src/pages/ArticleDetailPage.jsx]]) and the full
// ArticleOutlinePage split view ([[frontend/src/pages/ArticleOutlinePage.jsx]]).
// Both stay in sync on dirty semantics, gap-splice positions and jump matching —
// keep the logic here so they can't drift.
import { useState, useEffect } from "react";
import * as api from "../lib/api";

export const SOURCE_BADGE = {
  gsc: { label: "GSC", cls: "bg-green-50 text-green-700" },
  fanout: { label: "Fan-out", cls: "bg-purple-50 text-purple-700" },
  topic: { label: "Topic", cls: "bg-zinc-100 text-zinc-500" },
};

export function useArticleOutline(articleId) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [outline, setOutline] = useState(null);
  const [original, setOriginal] = useState(null);
  const [gapsLoading, setGapsLoading] = useState(false);
  const [gaps, setGaps] = useState(null);
  const [gapError, setGapError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getArticleOutline(articleId);
        if (cancelled) return;
        const sections = (data.sections || []).map((s) => ({ ...s }));
        setOutline({ title: data.title, sections });
        setOriginal({ title: data.title, sections: sections.map((s) => ({ ...s })) });
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "Could not extract outline");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [articleId]);

  const dirty = Boolean(
    outline && original &&
    JSON.stringify(outline.sections.map((s) => [s.heading, s.added])) !==
      JSON.stringify(original.sections.map((s) => [s.heading, s.added]))
  );

  // Any structural edit invalidates previously computed gaps: jump markers
  // reference adjacent heading pairs that may no longer be adjacent.
  const move = (i, dir) => {
    setGaps(null);
    setOutline((o) => {
      const sections = [...o.sections];
      const j = i + dir;
      if (j < 0 || j >= sections.length) return o;
      [sections[i], sections[j]] = [sections[j], sections[i]];
      return { ...o, sections };
    });
  };
  const remove = (i) => {
    setGaps(null);
    setOutline((o) => ({ ...o, sections: o.sections.filter((_, idx) => idx !== i) }));
  };

  const findGaps = async () => {
    setGapsLoading(true);
    setGapError(null);
    try {
      const result = await api.getArticleOutlineGaps(articleId, outline.sections);
      setGaps(result);
    } catch (e) {
      setGapError(e.response?.data?.detail || "Gap analysis failed");
    } finally {
      setGapsLoading(false);
    }
  };

  const addGap = (gap) => {
    setOutline((o) => {
      const sections = [...o.sections];
      const at = gap.position != null && gap.position + 1 <= sections.length ? gap.position + 1 : sections.length;
      sections.splice(at, 0, {
        key: `new-${gap.heading}`,
        heading: gap.heading,
        level: 2,
        words: 0,
        summary: "",
        added: true,
        note: gap.answers,
      });
      return { ...o, sections };
    });
    setGaps((g) => ({ ...g, missing: g.missing.filter((m) => m !== gap) }));
  };

  const dismissGap = (gap) => setGaps((g) => ({ ...g, missing: g.missing.filter((m) => m !== gap) }));

  // Jump marker between section i and i+1, if gap analysis flagged a break in
  // the thread — matched by exact equality of adjacent headings.
  const jumpAt = (i) => {
    if (!outline || !gaps?.jumps) return null;
    return gaps.jumps.find(
      (j) => j.between?.[0] === outline.sections[i]?.heading && j.between?.[1] === outline.sections[i + 1]?.heading
    ) || null;
  };

  const toPayload = () =>
    outline.sections.map((s) => ({ heading: s.heading, level: s.level, added: !!s.added, note: s.note || "" }));

  return {
    loading,
    error,
    title: outline?.title ?? null,
    totalWords: (outline?.sections || []).reduce((n, s) => n + (s.words || 0), 0),
    sections: outline?.sections ?? [],
    originalSections: original?.sections ?? [],
    ready: Boolean(outline),
    dirty,
    move,
    remove,
    gapsState: { gaps, gapsLoading, gapError },
    findGaps,
    addGap,
    dismissGap,
    jumpAt,
    toPayload,
  };
}
